import { Leaderboard } from "@/components/QuestsV2/Points/Leaderboard/Leaderboard";
import {
  type AllDualVariantIconKeys,
  Body,
  Caption,
  CountdownTimer,
  Divider,
  Heading,
  HorizontalMenu,
  Icon,
  Stack,
  useWindowSizeStore,
} from "@biom3/react";
import type { Content } from "@prismicio/client";
import type { RichTextField } from "@prismicio/client";
import { BREAKPOINTS } from "@ui-kit";
import type { WishlistProps } from "@ui-kit/GameSpecs";
import { RiveFollowButton } from "@ui-kit/RiveFollowButton";
import { useState } from "react";
import { useCampaign } from "../CampaignProvider";
import { XCreatorLeaderboardHeader } from "../X/XCreatorLeaderboardPrompt";
import type { Campaign, CampaignContent } from "../types";
import { CampaignHistoryTable } from "./CampaignHistory";
import { CampaignQuests } from "./CampaignQuests";
import { CampaignRules } from "./CampaignRules";
import {
  LeaderboardProvider,
  useLeaderboard,
} from "./Leaderboard/LeaderboardProvider";

type Tab = "quests" | "leaderboard" | "rules";

type PointsCampaignProps = {
  game: Content.GameDocument;
  wishlistProps: WishlistProps;
  visibleCampaignContent: CampaignContent;
};

function CampaignDetailsItem({
  icon,
  text,
}: {
  icon: AllDualVariantIconKeys;
  text: string;
}) {
  return (
    <Stack
      sx={{
        backgroundColor: "base.color.translucent.emphasis.100",
        borderRadius: "base.borderRadius.x8",
        textAlign: "center",
        padding: "base.spacing.x3",
        justifyContent: "center",
        alignItems: "center",
        flex: 1,
      }}
    >
      <Icon icon={icon} variant="bold" sx={{ width: "base.icon.size.300" }} />
      <Body size="xSmall">{text}</Body>
    </Stack>
  );
}

export const PointsCampaign = ({
  game,
  wishlistProps,
  visibleCampaignContent,
}: PointsCampaignProps) => {
  return (
    <LeaderboardProvider
      campaignId={visibleCampaignContent.campaign.id}
      isCreatorLeaderboard={visibleCampaignContent.campaign.type === "creator"}
    >
      <PointsCampaignContent
        game={game}
        wishlistProps={wishlistProps}
        campaignContent={visibleCampaignContent}
      />
    </LeaderboardProvider>
  );
};

const PointsCampaignContent = ({
  campaignContent,
  game,
  wishlistProps,
}: {
  campaignContent: CampaignContent;
  game: Content.GameDocument;
  wishlistProps: WishlistProps;
}) => {
  const { state: height } = useWindowSizeStore((store) => store.height);
  const renderAvatar = Boolean(height && height > BREAKPOINTS.large);
  const campaign = campaignContent.campaign;
  const { campaignHistory } = useCampaign();
  const [showCampaignHistoryModal, setShowCampaignHistoryModal] =
    useState(false);

  const isCreatorLeaderboard = campaign.type === "creator";

  return (
    <Stack
      sx={{
        position: "sticky",
        top: "base.spacing.x20",
        background: "base.color.translucent.inverse.500",
        borderRadius: "base.borderRadius.x8",
        padding: "base.spacing.x6",
        gap: "base.spacing.x4",
        maxHeight: "calc(100vh - 13rem)",
        overflowY: "scroll",
      }}
    >
      {!isCreatorLeaderboard &&
        campaign.startDate < new Date() &&
        campaign.endDate > new Date() && (
          <Heading
            size="medium"
            sx={{
              textAlign: "center",
              color: "base.color.text.heading.secondary",
            }}
          >
            {campaignContent.campaignDoc.data.tagline}
          </Heading>
        )}
      {isCreatorLeaderboard && (
        <XCreatorLeaderboardHeader
          gameId={game.uid}
          wishlistProps={wishlistProps}
        />
      )}

      {!wishlistProps.isWishlisted && (
        <RiveFollowButton
          isWishlisted={wishlistProps.isWishlisted}
          isLoading={false}
          onClick={() => {
            wishlistProps.onWishlistClick("PointsCampaign");
          }}
          game={game}
        />
      )}

      {campaign.startDate > new Date() ? (
        <ComingSoon campaign={campaign} />
      ) : campaign.endDate < new Date() ? (
        <ExpiredCampaignContent
          richTextField={
            campaignContent.campaignDoc.data.campaign_ended_message
          }
        />
      ) : (
        <LiveCampaignContent
          campaign={campaign}
          game={game}
          wishlistProps={wishlistProps}
          renderAvatar={renderAvatar}
          rules={campaignContent.campaignDoc.data.rules}
        />
      )}
      {campaignHistory.length > 0 &&
        (showCampaignHistoryModal ? (
          <CampaignHistoryTable campaignHistory={campaignHistory} />
        ) : (
          <Body
            size="small"
            sx={{
              color: "base.color.text.body.secondary",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={() => {
              setShowCampaignHistoryModal(true);
            }}
          >
            Show previous campaigns
          </Body>
        ))}
    </Stack>
  );
};

const ComingSoon = ({ campaign }: { campaign: Campaign }) => {
  return (
    <Stack
      sx={{
        flex: 1,
        backgroundColor: "base.color.translucent.emphasis.200",
        borderRadius: "base.borderRadius.x8",
        padding: "base.spacing.x6",
      }}
    >
      <Stack direction="row" alignItems="center">
        <Caption
          sx={{
            color: "base.color.text.body.secondary",
            fontSize: "10px",
          }}
        >
          Campaign Starts in:
        </Caption>
        <Divider size="xSmall" sx={{ flex: 1 }} />
      </Stack>

      <CountdownTimer
        futureTarget={campaign.startDate}
        size="large"
        variant="inverse"
        justifyContent={"flex-start"}
      />
    </Stack>
  );
};

const ExpiredCampaignContent = ({
  richTextField,
}: {
  richTextField?: RichTextField;
}) => {
  const { leaderboard } = useLeaderboard();
  return (
    <Stack sx={{ pb: "base.spacing.x10" }}>
      {leaderboard && (
        <CampaignDetailsItem
          icon={"People"}
          text={`${leaderboard.totalParticipants === 0 ? "1" : leaderboard.totalParticipants} participants`}
        />
      )}
      <CampaignRules field={richTextField} />
    </Stack>
  );
};

const LiveCampaignContent = ({
  campaign,
  game,
  wishlistProps,
  renderAvatar,
  rules,
}: {
  campaign: Campaign;
  game: Content.GameDocument;
  wishlistProps: WishlistProps;
  renderAvatar: boolean;
  rules?: RichTextField;
}) => {
  const isCreatorLeaderboard = campaign.type === "creator";
  const defaultTab = isCreatorLeaderboard ? "leaderboard" : "quests";
  const [tab, setTab] = useState<Tab>(defaultTab);
  const {
    leaderboard,
    isLoading,
    invalidate: invalidateLeaderboard,
  } = useLeaderboard();

  const renderPointsPill = leaderboard?.userEntry;
  return (
    <>
      <Stack direction="row">
        {leaderboard?.userEntry !== undefined && (
          <CampaignDetailsItem
            icon={"People"}
            text={`You're currently ranked #${leaderboard.userEntry.rank}`}
          />
        )}
        {leaderboard && !leaderboard?.winCondition && (
          <CampaignDetailsItem
            icon={"People"}
            text={`${leaderboard.totalParticipants === 0 ? "1" : leaderboard.totalParticipants} participants`}
          />
        )}
        {renderPointsPill && (
          <CampaignDetailsItem
            icon={"Sparkle"}
            text={`${leaderboard?.userEntry?.balance} Points`}
          />
        )}
        <CampaignDetailsItem
          icon={"Calendar"}
          text={`${Math.ceil((campaign.endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days left`}
        />
      </Stack>

      <HorizontalMenu size="small">
        {!isCreatorLeaderboard && (
          <HorizontalMenu.Button
            selected={tab === "quests"}
            onClick={() => setTab("quests")}
          >
            Quests
          </HorizontalMenu.Button>
        )}
        <HorizontalMenu.Button
          selected={tab === "leaderboard"}
          onClick={() => {
            setTab("leaderboard");
            invalidateLeaderboard?.();
          }}
        >
          Leaderboard
        </HorizontalMenu.Button>
        <HorizontalMenu.Button
          selected={tab === "rules"}
          onClick={() => setTab("rules")}
        >
          Rules
        </HorizontalMenu.Button>
      </HorizontalMenu>

      {tab === "quests" && (
        <CampaignQuests game={game} wishlistProps={wishlistProps} />
      )}
      {tab === "leaderboard" && (
        <Leaderboard
          entries={leaderboard?.topEntries ?? []}
          usersEntry={leaderboard?.userEntry}
          isLoading={isLoading}
          winCondition={leaderboard?.winCondition}
          isCreatorLeaderboard={isCreatorLeaderboard}
        />
      )}
      {tab === "rules" && <CampaignRules field={rules} />}
    </>
  );
};
